import type { JSX, ReactNode } from 'react';

/**
 * Interface primitives.
 *
 * Small, unopinionated pieces the app composes. Each keeps its own styling in
 * `app.css` on the tokens in `theme.css`, so a page file describes structure
 * rather than appearance and no component grows into a monolith.
 *
 * The house style is tactile (§20): controls stand off the page on a coloured
 * bottom edge and sink into it when pressed. That is one CSS rule, not a prop,
 * so nothing here has to remember to opt in.
 */

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  /**
   * `primary` is the sky-blue action, and there should be exactly one of
   * them in view. `sky` is the second-most-wanted thing on a page that already
   * has a primary — "practise" beside "open lesson" — and `secondary` is the
   * quiet raised white button for everything else.
   */
  variant?: 'primary' | 'secondary' | 'sky' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit';
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  'aria-label'?: string;
};

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  type = 'button',
  disabled = false,
  fullWidth = false,
  className = '',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`btn btn--${variant} btn--${size} ${fullWidth ? 'btn--full' : ''} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Card({
  children,
  className = '',
  as: Component = 'div',
  id,
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'article' | 'section' | 'li';
  /** For a card something links to by name — a town map's plate, say. */
  id?: string;
}): JSX.Element {
  return (
    <Component className={`card ${className}`} id={id}>
      {children}
    </Component>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'correct' | 'incorrect' | 'warn';
}): JSX.Element {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * A mastery bar.
 *
 * Mastery is the platform's primary metric (§10), so it gets one canonical
 * representation used on every surface — concept pages, course cards, the map
 * and the parent dashboard — and it always states the number in words for
 * screen readers rather than relying on the bar alone.
 */
export function MasteryBar({
  value,
  label,
  size = 'md',
  showValue = true,
}: {
  value: number;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  showValue?: boolean;
}): JSX.Element {
  const percent = Math.round(Math.min(1, Math.max(0, value)) * 100);
  return (
    <div className={`mastery mastery--${size}`}>
      {label ? <span className="mastery__label">{label}</span> : null}
      <div
        className="mastery__track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label ? `${label}: ` : ''}${percent}% mastery`}
      >
        <div className="mastery__fill" style={{ width: `${percent}%` }} />
      </div>
      {showValue ? <span className="mastery__value">{percent}%</span> : null}
    </div>
  );
}

/**
 * A ring showing progress towards a goal.
 *
 * Used where the target is a small whole number — days practised this week —
 * and a bar would imply more precision than the thing has. Drawn as an SVG
 * rather than with a conic gradient so it prints, scales and survives a
 * high-contrast theme.
 */
export function ProgressRing({
  value,
  label,
  caption,
  size = 76,
}: {
  /** 0–1. Values outside the range are clamped rather than drawn wrongly. */
  value: number;
  /** The big number in the middle. */
  label: ReactNode;
  /** Read out for screen readers, and shown under the ring when given. */
  caption: string;
  size?: number;
}): JSX.Element {
  const fraction = Math.min(1, Math.max(0, value));
  const stroke = Math.max(5, Math.round(size / 11));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="ring" style={{ width: size, ['--ring-size' as string]: `${size}px` }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={caption}>
        <circle className="ring__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} fill="none" />
        <circle
          className="ring__fill"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={`${circumference * fraction} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <span className="ring__label" aria-hidden="true">
        {label}
      </span>
      <span className="ring__caption">{caption}</span>
    </div>
  );
}

export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }): JSX.Element {
  return (
    <div className="stat">
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
      {hint ? <span className="stat__hint">{hint}</span> : null}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  headingLevel = 3,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  /**
   * Which heading this title is.
   *
   * An empty state usually stands inside a section that already has one, so
   * `h3` is the default. A page whose *whole* body is the empty state has no
   * such section, and an `h3` sitting directly under the `h1` is a hole in the
   * outline — which is how a screen reader user navigates.
   */
  headingLevel?: 2 | 3 | 4;
}): JSX.Element {
  const Heading = `h${headingLevel}` as const;
  return (
    <div className="empty-state">
      <Heading className="empty-state__title">{title}</Heading>
      <p className="empty-state__description">{description}</p>
      {action}
    </div>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <header className="section-heading">
      <div>
        {eyebrow ? <p className="section-heading__eyebrow">{eyebrow}</p> : null}
        <h2 className="section-heading__title">{title}</h2>
        {description ? <p className="section-heading__description">{description}</p> : null}
      </div>
      {action}
    </header>
  );
}

/** A short label for the status of a concept in the graph. */
export function StatusPill({ status }: { status: 'mastered' | 'in-progress' | 'ready' | 'locked' | 'planned' }): JSX.Element {
  const copy: Record<typeof status, string> = {
    mastered: 'Mastered',
    'in-progress': 'In progress',
    ready: 'Ready to start',
    locked: 'Locked',
    planned: 'Coming soon',
  };
  return <span className={`pill pill--${status}`}>{copy[status]}</span>;
}
